import { useEffect, useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'

interface StatItemProps {
  end: number
  suffix?: string
  label: string
  delay: number
}

function StatItem({ end, suffix = '', label, delay }: StatItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isInView) return

    const duration = 1500 // 1.5 seconds
    const startTime = performance.now()
    const startDelay = delay * 1000

    const timer = setTimeout(() => {
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime - startDelay
        const progress = Math.min(elapsed / duration, 1)

        // Ease out cubic
        const easeOut = 1 - Math.pow(1 - progress, 3)
        const currentCount = Math.floor(easeOut * end)

        setCount(currentCount)

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          setCount(end)
        }
      }

      requestAnimationFrame(animate)
    }, startDelay)

    return () => clearTimeout(timer)
  }, [isInView, end, delay])

  return (
    <motion.div
      ref={ref}
      className="stat-item"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
    >
      <div className="stat-number">
        {count}{suffix}
      </div>
      <div className="stat-label">{label}</div>
    </motion.div>
  )
}

export function StatsCounter() {
  return (
    <section className="stats-section">
      <div className="stats-grid">
        <StatItem end={60} suffix="+" label="Betting Markets" delay={0} />
        <StatItem end={6} label="Major Leagues" delay={0.15} />
        <StatItem end={31} label="Bookmakers" delay={0.3} />
        <StatItem end={2} suffix=" min" label="Live Refresh" delay={0.45} />
      </div>
    </section>
  )
}
